/**
 * C1b's calls (`KLQ-20260820-001` phase 3).
 *
 * THREE THINGS THE CONTRACT SAYS THAT THIS FILE IS BUILT AROUND
 *
 * 1. **The viewer never asks for the drawing's bytes.** ADR-0005 keeps the API out of the byte
 *    path and ADR-0006 exists so an A0 sheet opens like an A4; a `readDownloadUrl` on the
 *    `rendition_pdf` would defeat both at once. Downloads are a deliberate user action, and
 *    they are the only thing here that touches the original.
 * 2. **Tiles are signed in rectangles, never one at a time.** `columns` and `rows` are
 *    inclusive ranges (`"3-11"`), bounded by `MAX_TILES_PER_REQUEST` = 256 server-side. A
 *    viewport is a rectangle, so a screenful is one call.
 * 3. **The rendition response bootstraps the canvas.** Since E7 it carries per-page geometry
 *    and the already-signed single-tile ladder, so the viewer can construct a tile source and
 *    paint without a second round trip. `manifest_url` and `descriptor_urls` remain the
 *    fallback for a rendition with no stored geometry and are otherwise never fetched.
 */

import { api, unwrap } from '@/api/client';
import type { components } from '@/api/generated/openapi';

import type { SheetAnchor } from './overlay';

type Schemas = components['schemas'];

export type Rendition = Schemas['RenditionResponse'];
export type RenditionState = Rendition['state'];
export type RenditionPage = Schemas['RenditionPageResponse'];
export type SignedTile = Schemas['SignedTileResponse'];
export type TileBatch = Schemas['TileBatchResponse'];
export type Version = Schemas['VersionResponse'];
export type VersionFile = Schemas['VersionFileResponse'];
export type Pin = Schemas['PinResponse'];
export type PinPage = Schemas['Page_PinResponse_'];
export type Markup = Schemas['MarkupResponse'];
export type MarkupCreate = Schemas['MarkupCreateRequest'];
export type MarkupPage = Schemas['Page_MarkupResponse_'];

/** The pyramid's state, and — once `ready` — everything needed to open it. */
export function readRendition(blobRefId: string): Promise<Rendition> {
  return unwrap(
    api.GET('/api/v1/files/{blob_ref_id}/rendition', {
      params: { path: { blob_ref_id: blobRefId } },
    }),
  );
}

/**
 * Ask for a pyramid that does not exist yet.
 *
 * `202`, and idempotent on `(workspace, blob, kind)` — a second press starts no second build.
 * Offered on `absent` and on `failed`, because a failed render is often a transient sandbox
 * timeout and the alternative on offer is a PDF download.
 */
export function requestRendition(blobRefId: string): Promise<Rendition> {
  return unwrap(
    api.POST('/api/v1/files/{blob_ref_id}/rendition', {
      params: { path: { blob_ref_id: blobRefId } },
    }),
  );
}

export interface TileRect {
  readonly page: number;
  readonly level: number;
  readonly firstColumn: number;
  readonly lastColumn: number;
  readonly firstRow: number;
  readonly lastRow: number;
}

/** `3` or `3-11`, inclusive — the shape `_parse_range` in the files router accepts. */
function range(first: number, last: number): string {
  return first === last ? String(first) : `${String(first)}-${String(last)}`;
}

export function signTiles(blobRefId: string, rect: TileRect): Promise<TileBatch> {
  return unwrap(
    api.GET('/api/v1/files/{blob_ref_id}/tiles', {
      params: {
        path: { blob_ref_id: blobRefId },
        query: {
          page: rect.page,
          level: rect.level,
          columns: range(rect.firstColumn, rect.lastColumn),
          rows: range(rect.firstRow, rect.lastRow),
        },
      },
    }),
  );
}

/** One version and its files. The viewer's entry point: `?rev=` names it, or the slot's current. */
export function readVersion(versionId: string): Promise<Version> {
  return unwrap(
    api.GET('/api/v1/versions/{version_id}', {
      params: { path: { version_id: versionId } },
    }),
  );
}

/**
 * Every pin on the drawing — which is to say, on every version of it.
 *
 * **One call per slot, not per version, and that is the whole of carry-forward.** The address
 * is `slot_id`, so a pin dropped on r01 comes back while r04 is open, carrying the counter of
 * the version it came from in `source_version_counter`. That field exists precisely so the
 * *"from r03"* badge costs nothing: without it the viewer would fetch a version per pin to
 * render a number the server already had in hand.
 *
 * `include_resolved` is left at the server's default. A resolved pin is drawn differently
 * rather than dropped — the topic was closed, the place on the sheet it was about did not move
 * — and asking twice to get both halves would be two requests for one list.
 */
export function listPins(projectId: string, slotId: string): Promise<PinPage> {
  return unwrap(
    api.GET('/api/v1/projects/{project_id}/pins', {
      params: { path: { project_id: projectId }, query: { slot_id: slotId } },
    }),
  );
}

export interface PinBody {
  readonly slot_id: string;
  /** Normalised `0..1`, plus the page it was placed on. Never a pixel — Approach §5. */
  readonly anchor: SheetAnchor;
  /** The first thing said about the pin. Sent with it, in the same request. */
  readonly body: string;
}

/**
 * Drop a pin and say the first thing about it — **one request**.
 *
 * `PinCreateRequest.body` exists for exactly this: *"so that 'drop a pin and type a comment' is
 * one transaction rather than two requests with a window between them in which an unexplained
 * pin is visible to everybody on the project"*. So this screen never posts a pin and then a
 * comment; the composer holds both until there is something to say, and a person who changes
 * their mind leaves nothing behind.
 *
 * **`kind` is `comment` and nothing else is offered.** The enum's other two members — `task`
 * and `issue` — belong to `MCK-20260521-008`, which has no model, no service and no screen, and
 * the annotations service refuses them with a `409` naming that spec. An affordance that always
 * fails is worse than no affordance, so there is no kind picker here to fail with.
 * (`question` is the other MVP kind and is a distinction this screen has nowhere to make: the
 * rail renders one conversation and the design's Comment mode places one sort of pin.)
 *
 * `anchor_type` is sent explicitly rather than left to the server's default, because the seam
 * it names is the whole reason `PinResponse` has no `x`: a client that says which kind of
 * anchor it is sending is the client that keeps working the day `element` anchors land.
 *
 * **`audience` is sent explicitly too, and for a stronger reason than the seam.** It is the
 * safety property of `MCK-20260814-001`: a pin's first comment is written for the author's own
 * workspace unless somebody says otherwise, and a default that lives only on the server is a
 * default the next writer of this function picks differently. Until the composer offers the
 * choice (phase 4) the value is fixed here, where it is visible, rather than omitted and
 * inferred.
 *
 * `source_version_id` is **not** sent and cannot be: the server reads the slot's current
 * version at the moment of the write. That is why this screen only offers to place a pin while
 * the current version is the one on screen — see `ViewerPage`.
 */
export function createPin(projectId: string, pin: PinBody): Promise<Pin> {
  return unwrap(
    api.POST('/api/v1/projects/{project_id}/pins', {
      params: { path: { project_id: projectId } },
      body: {
        slot_id: pin.slot_id,
        kind: 'comment',
        audience: 'internal',
        anchor_type: 'coordinate',
        anchor_ref: { x: pin.anchor.x, y: pin.anchor.y, page: pin.anchor.page },
        body: pin.body,
      },
    }),
  );
}

/**
 * That version's markup, and no other's.
 *
 * **Keyed on the version, not the slot.** Markup does not carry forward: a stroke drawn on
 * r03 is not on r04. The query key expresses that, and the server enforces it with the
 * foreign key on `markup.version_id`.
 */
export function listMarkups(versionId: string): Promise<MarkupPage> {
  return unwrap(
    api.GET('/api/v1/markups', {
      params: { query: { version_id: versionId } },
    }),
  );
}

/**
 * Draw a stroke on one version.
 *
 * No `PATCH`: a stroke is changed by removing it and drawing it again (`deleteMarkup`).
 *
 * No `Idempotency-Key`: the endpoint declares `header?: never` and takes none. `retry: false`
 * on the mutation is the whole protection a replayed stroke gets.
 */
export function createMarkup(body: MarkupCreate): Promise<Markup> {
  return unwrap(
    api.POST('/api/v1/markups', {
      body,
    }),
  );
}

/**
 * Take back one stroke — the eraser, clear and undo (owner, 2026-09-17).
 *
 * The server lets only the stroke's author do it (`403` otherwise) and forgets the row
 * entirely. `404` is a stroke already gone or out of reach; the caller treats it as done.
 */
export function deleteMarkup(markupId: string): Promise<unknown> {
  return unwrap(
    api.DELETE('/api/v1/markups/{markup_id}', {
      params: { path: { markup_id: markupId } },
    }),
  );
}
